<script setup lang="ts">
import { onBeforeMount, ref, computed } from 'vue';
import { useUnderwritingStore } from '@/stores/underwriting';
import { usePortfolioStore } from '@/stores/portfolio';
import { useGlobalStore } from '@/stores/global';
import { useActuarialStore } from '@/stores/actuarial';
import { useDashboardStore } from '@/stores/dashboard';
import { api } from '@/services/api';
import moment from 'moment';
import { openSuccessMsg } from '@/utilities/notificationMessage';
import errorHandler from '@/utilities/errorhandler';
import DialogComponent from '@/components/Dialog/DialogComponent.vue';
import SwitchComponent from '@/components/SwitchComponent.vue';
import JetButton from '@/components/JetButton.vue';
import generateRandomKey from '@/utilities/generateRandomKey';

const underwritingStore = useUnderwritingStore();
const portfolioStore = usePortfolioStore();
const globalStore = useGlobalStore();
const actuarialStore = useActuarialStore();

const originalControllerData = ref({});
const controllerData = ref({});

const PREVIOUS = 0;
const CURRENT = 1;
const NEXT = 2;
function percentageFormat(val: number) {
  return (val * 100).toFixed(1) + '%';
}

const applyRate = ref(portfolioStore.parameters['claims_nature'].slice(1).map(() => false));
const applyRateOriginalValue = ref(portfolioStore.parameters['claims_nature'].slice(1).map(() => null));

function onClickApplyRate(cd: any, idx: number) {
  if (!applyRate.value[idx]) {
    onClickValueDynamicDialog(cd);
  }
}

function applyRateChangeImpact(cd: string, isApplyRate: boolean) {
  if (!isApplyRate) {
    dynamicModalEnter(applyRateOriginalValue.value[cd] * 100, cd);
  } else {
    if (!applyRateOriginalValue.value[cd]) {
      applyRateOriginalValue.value[cd] =
        controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[toTitleCase(cd) + ' Load']];
    }

    let newLoads =
      controllerData.value['value_array'][PREVIOUS][underwritingStore.valueArrayColumns[toTitleCase(cd) + ' Load']] /
      (1 +
        controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Rate Change']] *
          controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Credit']]);

    if (!newLoads) {
      newLoads = 0;
    }

    dynamicModalEnter(newLoads * 100, cd);
  }
}

async function ceaseUncease() {
  changes.value = Object.keys(underwritingStore.valueArrayColumns).reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});

  if (controllerData.value['value_array'][NEXT][underwritingStore.valueArrayColumns['Exits']]) {
    changes.value['Exits'] = 0;
  } else {
    changes.value['Exits'] = 1;
  }
  await submitUnderwritingController();
  changes.value = Object.keys(underwritingStore.valueArrayColumns).reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});
  // globalStore.setLoading(true);
  // await api
  //   .post(import.meta.env.VITE_API_URL + "underwriting/cease-underwriting", {
  //     bounce_id: portfolioStore.selectedBounceID,
  //     filters: underwritingStore.underwritingControllerDate["filters"],
  //     underwriting_month: underwritingStore.underwritingControllerDate["underwriting_month"],
  //     claims_nature: portfolioStore.parameters["claims_nature"],
  //     commission: portfolioStore.parameters["Commission"],
  //     changes: changes.value,
  //     exit_value: isNextMonth.value ? 0 : 1
  //   })
  //   .then((res) => {
  //     globalStore.setLoading(false);
  //     if (res.data.data.data.is_new_bounce) {
  //       portfolioStore.changeBounceID(res.data.data.data.bounce_id)
  //     }
  //     underwritingStore.fetchType()
  //   })
  //   .catch(() => {
  //     // openErrorMsg("Error!")
  //     globalStore.setLoading(false);
  //     globalStore.onConnectionError()
  //   })
}

async function submitUnderwritingController() {
  underwritingStore.updateIsUnderwritingControllerModal(false);
  globalStore.setLoading(true);

  let apiChangesBody = {};
  const TO_BE_PARSED = [
    'GWP',
    'Attritional_LR',
    'HIST_ACTIONS',
    'HIST_ACTIONS_CREDITS',
    'INDEXES',
    'EXITS',
    'ELASTICITY',
    'MANUAL_PERTURBATION',
    'ALGO_PERTURBATION',
  ];
  const CHANGES_ORIGINAL_NAME = [
    'GWP',
    'Attritional A-priori',
    'Rate Change',
    'Credit',
    'Indexation',
    'Exits',
    'Elasticity',
    'Manual Perturbation',
    'Algorithmic Perturbation',
  ];
  // Convert changes to api body

  for (let i = 0; i < TO_BE_PARSED.length; i++) {
    if (changes.value[CHANGES_ORIGINAL_NAME[i]] != null) {
      apiChangesBody[TO_BE_PARSED[i]] = changes.value[CHANGES_ORIGINAL_NAME[i]];
    }
  }

  for (const i of portfolioStore.parameters['line_size']) {
    if (changes.value[i] != null) {
      apiChangesBody[i] = changes.value[i];
    }
  }

  for (const i of Object.keys(additionalChanges.value['Commission'])) {
    apiChangesBody[i] = additionalChanges.value['Commission'][i];
  }

  for (const i of Object.keys(additionalChanges.value['Loads'])) {
    apiChangesBody[i] = additionalChanges.value['Loads'][i];
  }

  const a = {
    bounce_id: portfolioStore.selectedBounceID,
    filters: underwritingStore.underwritingControllerDate['filters'],
    underwriting_month: underwritingStore.underwritingControllerDate['underwriting_month'],
    claims_nature: portfolioStore.parameters['claims_nature'],
    commission: portfolioStore.parameters['Commission'],
    changes: changes.value,
  };
  console.log(a);
  await api
    .post(import.meta.env.VITE_API_URL + 'underwriting/submit-table-controller', {
      bounce_id: portfolioStore.selectedBounceID,
      filters: underwritingStore.underwritingControllerDate['filters'],
      underwriting_month: underwritingStore.underwritingControllerDate['underwriting_month'],
      changes: apiChangesBody,
      selectedLineSize: portfolioStore.selectedLineSize,
    })
    .then(async (res) => {
      globalStore.setLoading(false);
      openSuccessMsg('Changes saved successfully!');
      actuarialStore.createDraft(res);
      actuarialStore.setActuarialCacheResetRequired(true);
      return Promise.all([
        underwritingStore.fetchUnderwritingTable(),
        portfolioStore.onResetForwardLooking(true),
        portfolioStore.getSignedOff(),
      ]);
    })
    .then(() => {
      globalStore.setLoading(false);
    })
    .catch((error) => {
      // openErrorMsg("Error!")
      errorHandler(error);
      portfolioStore.getSignedOff();
      globalStore.setLoading(false);
      globalStore.onConnectionError();
    });
}

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

function mouseLeaveController(type: string) {
  isActive.value[type] = false;
  // isChanged.value[type] ? (changedClass.value[type] = true) : '';
}

function mouseOverCurrMonth(type: string) {
  isActive.value[type] = true;
  // changedClass.value[type] = false
}
const dashboardStore = useDashboardStore();

function checkIsProj(val: string) {
  if (moment(val, 'YYYY-MM-DD').isSameOrBefore(moment(dashboardStore.report_date))) {
    return false;
  } else {
    return true;
  }
}

const isProjPrev = computed(() =>
  checkIsProj(
    moment(underwritingStore.underwritingControllerDate['underwriting_month'], 'YYYY-MM-DD')
      .subtract(1, 'months')
      .format('YYYY-MM-DD')
  )
);
const isProjCurr = computed(() => checkIsProj(underwritingStore.underwritingControllerDate['underwriting_month']));
const isProjNext = computed(() =>
  checkIsProj(
    moment(underwritingStore.underwritingControllerDate['underwriting_month'], 'YYYY-MM-DD')
      .add(1, 'months')
      .format('YYYY-MM-DD')
  )
);

const changes = ref(
  Object.keys(underwritingStore.valueArrayColumns).reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {})
);

const additionalChanges = ref({ Commission: {}, Loads: {}, CP: {} });

const anyChange = computed(() => {
  return !Object.values(changes.value).every((value) => value === null);
});

async function fetchTableController() {
  globalStore.setLoading(true);
  let data = null;

  let post = {
    bounce_id: portfolioStore.selectedBounceID,
    filters: underwritingStore.underwritingControllerDate['filters'],
    underwriting_month: underwritingStore.underwritingControllerDate['underwriting_month'],
    changes: changes.value,
    selectedLineSize: portfolioStore.selectedLineSize,
    value_array_cols: underwritingStore.valueArrayColumns,
    anyChange: anyChange.value,
  };

  await api
    .post(import.meta.env.VITE_API_URL + 'underwriting/fetch-table-controller', post)
    .then((res) => {
      data = res.data.data;
      globalStore.setLoading(false);
    })
    .catch(() => {
      // openErrorMsg("Error!")
      globalStore.setLoading(false);
      globalStore.onConnectionError();
    });

  return data;
}

async function fetchDynamicController(variable: string) {
  globalStore.setLoading(true);
  let data = null;

  let post = {
    bounce_id: portfolioStore.selectedBounceID,
    filters: underwritingStore.underwritingControllerDate['filters'],
    underwriting_month: underwritingStore.underwritingControllerDate['underwriting_month'],
    changes: changes.value,
    selectedLineSize: portfolioStore.selectedLineSize,
    anyChange: anyChange.value,
    variable: variable,
  };

  await api
    .post(import.meta.env.VITE_API_URL + 'underwriting/fetch-dynamic-controller', post)
    .then((res) => {
      data = res.data.data;
      globalStore.setLoading(false);
    })
    .catch(() => {
      // openErrorMsg("Error!")
      globalStore.setLoading(false);
      globalStore.onConnectionError();
    });

  return data;
}

onBeforeMount(async () => {
  const data = await fetchTableController();
  if (data != undefined) {
    originalControllerData.value = JSON.parse(JSON.stringify(data));
    controllerData.value = JSON.parse(JSON.stringify(data));
  }
  applyRate.value = portfolioStore.parameters['claims_nature'].slice(1).map((x) => false);
});

async function dynamicColumnValues(e: any) {
  dynamicDialogData.value = [];
  dynamicDialogEdit.value = {};

  let data = null;
  data = await fetchDynamicController(e);

  if (data == null) {
    return;
  }

  dynamicDialogType.value = e;

  if (e == 'Commission' || e == 'Loads') {
    for (const i in data['variable']) {
      dynamicDialogData.value.push(data['variable'][i]);
      dynamicDialogEdit.value[data['variable'][i]] = {
        isEditing: false,
        value: data['value'][i],
      };
    }
  }
}

function onDynamicModal(e: any) {
  dynamicColumnValues(e);
  dynamicDialogVisible.value = true;
}

function calculateTotalDynamicModal() {
  let sum = 0;
  for (const i of dynamicDialogData.value) {
    sum +=
      additionalChanges.value[dynamicDialogType.value][i] != null
        ? additionalChanges.value[dynamicDialogType.value][i]
        : dynamicDialogEdit.value[i].value;
  }
  return sum;
}

function keyupEnter(e) {
  e.target.blur();
}

function keyUpInput(e) {
  tempInput.value = e.target.value;
}

function onFocusController(e) {
  e.target.select();
}

const prevMonth = ref({
  actualOrProjection: 3,
  Loads: 1,
  Att: 1,
});

function chosenHierarchies() {
  return Object.values(underwritingStore.underwritingControllerDate['filters']) as object[];
}

function chosenHierarchiesValue(idx: string, val) {
  if (val.length == 1) {
    return portfolioStore.dictionary[idx][val[0]];
  } else {
    return 'All';
  }
}

const selectedGWPUnit = computed<string>(() => globalStore.currency.currencyFormat.selectedGWPUnit);
const gwpUnits = computed(() => globalStore.currency.GWPUnits);

function gwpFormat(x: number) {
  return (x / parseFloat(gwpUnits.value[selectedGWPUnit.value][0])).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const amountType = computed(
  () =>
    globalStore.currency.currencyFormat['currency'] +
    globalStore.currency.GWPUnits[globalStore.currency.currencyFormat['selectedGWPUnit']][1]
);

const isActive = ref({ GWP: false });
const isChanged = ref({ GWP: false });
const isChangedPrev = ref({ GWP: false });
const changedClassPrev = ref({ GWP: false });

const tempInput = ref('');

const isEdit = ref({
  RateCredit: {
    isEditing: false,
    value: 100,
  },
  Elasticity: {
    isEditing: false,
    value: 100,
  },
  LineSize: {
    isEditing: false,
    value: 100,
  },
  RateChange: {
    isEditing: false,
    value: 100,
  },
  CP: {
    isEditing: false,
    value: 100,
  },
  Index: {
    isEditing: false,
    value: 100,
  },
  PP: {
    isEditing: false,
    value: 100,
  },
  GWP: {
    isEditing: false,
    value: 100,
  },
  Att: {
    isEditing: false,
    value: 100,
  },
  Loads: {
    isEditing: false,
    value: 100,
  },
  CombineLoads: {
    isEditing: false,
    value: 100,
  },
  CCR: {
    isEditing: false,
    value: 100,
  },
});

async function controllerUnitChange(e: any, changeType: string, valueType: string) {
  isEdit.value[changeType].isEditing = false;
  let val = 0;

  if (valueType == 'Elasticity') {
    val = parseFloat(parseFloat(e.target.value).toFixed(1));
  } else if (
    valueType == 'Rate Change' ||
    valueType == 'Credit' ||
    valueType == 'Manual Perturbation' ||
    valueType == 'Algorithmic Perturbation'
  ) {
    val = parseFloat((parseFloat(e.target.value) / 100).toFixed(4));
  } else {
    val = parseFloat((parseFloat(e.target.value) / 100).toFixed(4));
  }

  if (controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[valueType]] != val) {
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[valueType]] = val;
    if (valueType == portfolioStore.selectedLineSize) {
      controllerData.value['value_array'][NEXT][underwritingStore.valueArrayColumns[valueType]] =
        controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[valueType]];
      changes.value[valueType] = val;
    } else {
      changes.value[valueType] = val;
      controllerData.value = await fetchTableController();
      changes.value['Attritional A-priori'] = null;
    }
  }

  for (const i in portfolioStore.parameters['claims_nature'].slice(1)) {
    if (applyRate.value[i] == true) {
      applyRateChangeImpact(portfolioStore.parameters['claims_nature'].slice(1)[i], true);
    }
  }

  if (valueType == 'Attritional A-priori') {
    changes.value['Rate Change'] =
      controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Rate Change']];
  }

  highlightCheck();
}

function highlightCheck() {
  isChanged.value['CCR'] =
    (
      controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']] +
      controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Attritional A-priori']] +
      portfolioStore.parameters['claims_nature']
        .slice(1)
        .map(
          (x) =>
            controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']]
        )
        .reduce((s, a) => s + a, 0)
    ).toFixed(4) !=
    (
      originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']] +
      originalControllerData.value['value_array'][CURRENT][
        underwritingStore.valueArrayColumns['Attritional A-priori']
      ] +
      portfolioStore.parameters['claims_nature']
        .slice(1)
        .map(
          (x) =>
            originalControllerData.value['value_array'][CURRENT][
              underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
            ]
        )
        .reduce((s, a) => s + a, 0)
    ).toFixed(4);
  isChanged.value['NLR'] = isChanged.value['CCR'];
  isChanged.value['Att'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Attritional A-priori']].toFixed(
      4
    ) !=
    originalControllerData.value['value_array'][CURRENT][
      underwritingStore.valueArrayColumns['Attritional A-priori']
    ].toFixed(4);

  isChanged.value['GWP'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['GWP']].toFixed(0) !=
    originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['GWP']].toFixed(0);
  isChanged.value['Elasticity'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Elasticity']].toFixed(1) !=
    originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Elasticity']].toFixed(1);
  isChanged.value['RateChange'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Rate Change']].toFixed(4) !=
    originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Rate Change']].toFixed(4);
  isChanged.value['RateCredit'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Credit']].toFixed(4) !=
    originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Credit']].toFixed(4);
  isChanged.value['Index'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Indexation']].toFixed(4) !=
    originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Indexation']].toFixed(4);
  isChanged.value['CP'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Manual Perturbation']].toFixed(
      4
    ) !=
    originalControllerData.value['value_array'][CURRENT][
      underwritingStore.valueArrayColumns['Manual Perturbation']
    ].toFixed(4);
  isChanged.value['PP'] =
    controllerData.value['value_array'][CURRENT][
      underwritingStore.valueArrayColumns['Algorithmic Perturbation']
    ].toFixed(4) !=
    originalControllerData.value['value_array'][CURRENT][
      underwritingStore.valueArrayColumns['Algorithmic Perturbation']
    ].toFixed(4);
  isChanged.value['Commission'] =
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']].toFixed(4) !=
    originalControllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']].toFixed(4);
  isChanged.value['Loads'] =
    portfolioStore.parameters['claims_nature']
      .slice(1)
      .map(
        (x) =>
          controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']]
      )
      .reduce((s, a) => s + a, 0)
      .toFixed(4) !=
    portfolioStore.parameters['claims_nature']
      .slice(1)
      .map(
        (x) =>
          originalControllerData.value['value_array'][CURRENT][
            underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
          ]
      )
      .reduce((s, a) => s + a, 0)
      .toFixed(4);
}

function revertChange() {
  controllerData.value = structuredClone(originalControllerData.value);
  isChanged.value = { GWP: false };
  changes.value = Object.keys(underwritingStore.valueArrayColumns).reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});
  highlightCheck();
}

function onClickValueDynamicDialog(cd: string) {
  dynamicDialogEdit.value[cd].isEditing = true;
}

function dynamicModalEnter(e, cd: string) {
  if (dynamicDialogType.value == 'Commission') {
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']] +=
      parseFloat((parseFloat(e) / 100).toFixed(4)) -
      (additionalChanges.value[dynamicDialogType.value][cd]
        ? additionalChanges.value[dynamicDialogType.value][cd]
        : dynamicDialogEdit.value[cd].value);
    controllerData.value['value_array'][NEXT][underwritingStore.valueArrayColumns['Commission']] =
      controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']];
  } else {
    controllerData.value['value_array'][CURRENT][underwritingStore.valueArrayColumns[toTitleCase(cd) + ' Load']] =
      parseFloat((parseFloat(e) / 100).toFixed(4));
    controllerData.value['value_array'][NEXT][underwritingStore.valueArrayColumns[toTitleCase(cd) + ' Load']] =
      parseFloat((parseFloat(e) / 100).toFixed(4));
  }

  additionalChanges.value[dynamicDialogType.value][cd] = parseFloat((parseFloat(e) / 100).toFixed(4));
  if (dynamicDialogEdit.value[cd]) {
    dynamicDialogEdit.value[cd].isEditing = false;
  }

  highlightCheck();
}

const dynamicDialogVisible = ref(false);
const dynamicDialogType = ref('');
const dynamicDialogData = ref(['one', 'two']);
const dynamicDialogEdit = ref({ one: { isEditing: false, value: 10 }, two: { isEditing: false, value: 10 } });
</script>
<template>
  <DialogComponent
    v-model="underwritingStore.isUnderwritingControllerModal"
    dialog-style="width: 950px; min-width: 650px; background-color: rgb(243, 244, 246)"
  >
    <DialogComponent v-model="dynamicDialogVisible" title="" dialog-style="width: 800px;">
      <table
        v-if="dynamicDialogVisible"
        class="border-gray-500 shadow-lg"
        style="background-color: #fff; width: 100%; margin: 0 auto; border: 1px solid #eee"
      >
        <tbody>
          <tr class="w-1/2 font-bold bg-sybil-teal text-white border-gray-500">
            <th class="border-1 border-gray-500">{{ dynamicDialogType }} Type</th>
            <th class="border-1 border-gray-500">{{ dynamicDialogType }} Value</th>
            <th v-if="dynamicDialogType == 'Loads'">Rate Change Impact</th>
          </tr>
          <tr
            v-for="(cd, idx) in dynamicDialogData"
            :key="generateRandomKey(idx)"
            class="w-1/2 bg-white hover:bg-gray-200"
          >
            <td class="pl-5 text-center">{{ toTitleCase(cd.replace('COM_', '').replace(/_/g, ' ')) }}</td>
            <td class="text-center">
              <div
                class="cursor-pointer"
                @dblclick="onClickApplyRate(cd, idx)"
                @keyup.enter="keyupEnter"
                @focusout="(e: any) => dynamicModalEnter(e.target.value, cd)"
              >
                <div v-if="!dynamicDialogEdit[cd].isEditing" class="text-center">
                  {{
                    (
                      (additionalChanges[dynamicDialogType][cd] || additionalChanges[dynamicDialogType][cd] == 0
                        ? additionalChanges[dynamicDialogType][cd]
                        : dynamicDialogEdit[cd].value) * 100
                    ).toFixed(1)
                  }}%
                </div>
                <input
                  v-if="dynamicDialogEdit[cd].isEditing"
                  type="text"
                  :value="(dynamicDialogEdit[cd].value * 100).toFixed(1)"
                  style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                  class="form-control text-center manual-input text-sm"
                  @focus="onFocusController"
                />
              </div>
            </td>
            <td v-if="dynamicDialogType == 'Loads'" class="text-center">
              <SwitchComponent
                v-model="applyRate[idx]"
                @update:model-value="applyRateChangeImpact(cd, applyRate[idx])"
              />
            </td>
          </tr>
          <tr class="w-1/2 font-bold bg-sybil-teal bg-opacity-20">
            <td class="pl-5 text-center">Total</td>
            <td class="text-center">
              <div>
                <div class="text-center">
                  {{ percentageFormat(calculateTotalDynamicModal()) }}
                </div>
              </div>
            </td>
            <td v-if="dynamicDialogType == 'Loads'"></td>
          </tr>
        </tbody>
      </table>
    </DialogComponent>

    <template v-if="controllerData['UNDERWRITING_MONTH']">
      <table class="ml-5">
        <tbody>
          <tr>
            <td style="width: 200px">
              <div
                v-if="false"
                class="p-5 text-xl"
                style="background-color: #fff; width: 150px; margin: 0 auto; border: 1px solid #eee"
              >
                The first<br />month<br />controller<br />is not<br />editable,<br />changes on<br />this screen<br />can
                <b>NOT</b><br />be saved
              </div>
              <table
                v-if="controllerData['UNDERWRITING_MONTH'][PREVIOUS]"
                class="shadow-lg scale-75 hover:scale-100 transition ease-in-out duration-500"
                style="background-color: #fff; width: 150px; margin: 0 auto; border: 1px solid #eee"
              >
                <tbody>
                  <tr>
                    <th class="bg-sybil-teal text-white cursor-pointer py-1">
                      {{ controllerData['UNDERWRITING_MONTH'][PREVIOUS] }}
                    </th>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['GWP'],
                        flagChangeAnimation: isChangedPrev['GWP'],
                        flagChange: changedClassPrev['GWP'],
                      }"
                      class="border-b"
                      @mouseover="isActive['GWP'] = true"
                      @mouseleave="isActive['GWP'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        {{ isProjPrev ? 'Sys. Proj.' : 'Actual' }}
                        GWP
                        {{ amountType }}:
                        {{
                          gwpFormat(
                            originalControllerData['value_array'][PREVIOUS][underwritingStore.valueArrayColumns['GWP']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['Att'] }"
                      class="border-b"
                      @mouseover="isActive['Att'] = true"
                      @mouseleave="isActive['Att'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Att.A-priori:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['Commission'] }"
                      class="border-b"
                      @mouseover="isActive['Commission'] = true"
                      @mouseleave="isActive['Commission'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        {{ isProjPrev ? 'Sys. Proj.' : 'Actual' }} commission:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Commission']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['RateChange'] }"
                      class="border-b"
                      @mouseover="isActive['RateChange'] = true"
                      @mouseleave="isActive['RateChange'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Rate change:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Rate Change']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['RateCredit'] }"
                      class="border-b"
                      @mouseover="isActive['RateCredit'] = true"
                      @mouseleave="isActive['RateCredit'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Rate credit:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Credit']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['Elasticity'] }"
                      class="border-b"
                      @mouseover="isActive['Elasticity'] = true"
                      @mouseleave="isActive['Elasticity'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Elasticity:
                        {{
                          originalControllerData['value_array'][PREVIOUS][
                            underwritingStore.valueArrayColumns['Elasticity']
                          ].toFixed(1)
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['Index'] }"
                      class="border-b"
                      @mouseover="isActive['Index'] = true"
                      @mouseleave="isActive['Index'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Index.:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Indexation']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['CP'] }"
                      class="border-b"
                      @mouseover="isActive['CP'] = true"
                      @mouseleave="isActive['CP'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        M. Perturbation:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Manual Perturbation']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['PP'] }"
                      class="border-b"
                      @mouseover="isActive['PP'] = true"
                      @mouseleave="isActive['PP'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Alg. Perturbation:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Algorithmic Perturbation']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['LineSize'] }"
                      class="border-b"
                      @mouseover="isActive['LineSize'] = true"
                      @mouseleave="isActive['LineSize'] = false"
                    >
                      <div ref="LineSize-1" class="break-normal my-1 text-center cursor-default">
                        Line Size:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns[portfolioStore.selectedLineSize]
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      v-if="prevMonth['Loads']"
                      :class="{ hoverColor: isActive['Loads'] }"
                      class="border-b"
                      @mouseover="isActive['Loads'] = true"
                      @mouseleave="isActive['Loads'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Loads:
                        {{
                          percentageFormat(
                            portfolioStore.parameters['claims_nature']
                              .slice(1)
                              .map(
                                (x) =>
                                  originalControllerData['value_array'][PREVIOUS][
                                    underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                  ]
                              )
                              .reduce((s, a) => s + a, 0)
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['NLR'] }"
                      class="border-b"
                      @mouseover="isActive['CombineLoads'] = true"
                      @mouseleave="isActive['CombineLoads'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        NLR A-priori:
                        {{
                          percentageFormat(
                            (originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] +
                              portfolioStore.parameters['claims_nature']
                                .slice(1)
                                .map(
                                  (x) =>
                                    originalControllerData['value_array'][PREVIOUS][
                                      underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                    ]
                                )
                                .reduce((s, a) => s + a, 0)) /
                              (1 -
                                originalControllerData['value_array'][PREVIOUS][
                                  underwritingStore.valueArrayColumns['Commission']
                                ])
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['CCR'] }"
                      @mouseover="isActive['CCR'] = true"
                      @mouseleave="isActive['CCR'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        CCR A-priori:
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][PREVIOUS][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] +
                              portfolioStore.parameters['claims_nature']
                                .slice(1)
                                .map(
                                  (x) =>
                                    originalControllerData['value_array'][PREVIOUS][
                                      underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                    ]
                                )
                                .reduce((s, a) => s + a, 0) +
                              originalControllerData['value_array'][PREVIOUS][
                                underwritingStore.valueArrayColumns['Commission']
                              ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td style="width: 500px; margin: 0 auto">
              <table class="shadow-lg bg-white w-full">
                <tbody>
                  <tr>
                    <th colspan="6" class="bg-sybil-teal text-white text-center py-2">
                      {{ controllerData['UNDERWRITING_MONTH'][CURRENT] }}
                    </th>
                  </tr>
                  <tr v-for="(value, index) in chosenHierarchies()" :key="generateRandomKey(index)">
                    <td colspan="6" class="border-b break-normal px-2">
                      <label class="font-bold">{{ Object.keys(value)[0] }}:</label>
                      &nbsp;&nbsp;
                      <a>{{ chosenHierarchiesValue(Object.keys(value)[0], Object.values(value)[0]) }}</a>
                    </td>
                  </tr>
                  <tr>
                    <td
                      colspan="6"
                      class="break-normal px-2"
                      :class="{
                        hoverColor: isActive['LineSize'],
                        flagChangeAnimation: isChanged['LineSize'],
                        flagChange: isChanged['LineSize'],
                      }"
                      @mouseover="mouseOverCurrMonth('LineSize')"
                      @mouseleave="mouseLeaveController('LineSize')"
                      @contextmenu.prevent="revertChange()"
                    ></td>
                  </tr>

                  <tr>
                    <td colspan="3" class="bg-sybil-teal bg-opacity-20 pl-2">
                      <i>Starting Point</i>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['GWP'] }"
                      class="text-center border-r w-1/3"
                      @mouseover="isActive['GWP'] = true"
                      @mouseleave="isActive['GWP'] = false"
                    >
                      <label class="font-bold">{{ isProjCurr ? 'Sys. Proj.' : 'Actual' }} GWP {{ amountType }}</label>
                      <div>
                        {{
                          gwpFormat(
                            originalControllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['GWP']]
                          )
                        }}
                      </div>
                    </td>

                    <td
                      :class="{ hoverColor: isActive['Att'] }"
                      class="text-center border-r w-1/3"
                      @mouseover="isActive['Att'] = true"
                      @mouseleave="isActive['Att'] = false"
                    >
                      <label class="font-bold">Attritional A-priori</label>
                      <div>
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ]
                          )
                        }}
                      </div>
                    </td>

                    <td
                      :class="{ hoverColor: isActive['Commission'] }"
                      class="text-center w-1/3"
                      @mouseover="isActive['Commission'] = true"
                      @mouseleave="isActive['Commission'] = false"
                    >
                      <label class="font-bold">{{ isProjCurr ? 'Sys. Proj.' : 'Actual' }} Commission</label>
                      <div>
                        {{
                          percentageFormat(
                            originalControllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Commission']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3" class="bg-sybil-teal bg-opacity-20 pl-2">
                      <i>Calculate, double click to adjust</i>
                    </td>
                  </tr>
                  <tr></tr>
                  <tr class="border-b">
                    <td
                      :class="{
                        hoverColor: isActive['RateChange'],
                        flagChangeAnimation: isChanged['RateChange'],
                        flagChange: isChanged['RateChange'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('RateChange')"
                      @mouseleave="mouseLeaveController('RateChange')"
                      @contextmenu.prevent="revertChange()"
                    >
                      <label class="font-bold">Rate Change</label>
                      <div
                        class="cursor-pointer"
                        @dblclick="
                          isEdit['RateChange'].isEditing = true;
                          tempInput = (
                            controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Rate Change']] *
                            100
                          ).toFixed(1);
                        "
                        @keyup.enter="keyupEnter"
                        @focusout="(e) => controllerUnitChange(e, 'RateChange', 'Rate Change')"
                      >
                        <p v-if="!isEdit['RateChange'].isEditing">
                          {{
                            percentageFormat(
                              controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Rate Change']]
                            )
                          }}
                        </p>
                        <input
                          v-if="isEdit['RateChange'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="form-control text-center manual-input text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>
                    <td
                      :class="{
                        hoverColor: isActive['RateCredit'],
                        flagChangeAnimation: isChanged['RateCredit'],
                        flagChange: isChanged['RateCredit'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('RateCredit')"
                      @mouseleave="mouseLeaveController('RateCredit')"
                      @contextmenu.prevent="revertChange()"
                    >
                      <label class="font-bold">Rate Credit</label>
                      <div
                        class="cursor-pointer"
                        @dblclick="
                          isEdit['RateCredit'].isEditing = true;
                          tempInput = (
                            controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Credit']] * 100
                          ).toFixed(1);
                        "
                        @keyup.enter="keyupEnter"
                        @focusout="(e) => controllerUnitChange(e, 'RateCredit', 'Credit')"
                      >
                        <p v-if="!isEdit['RateCredit'].isEditing">
                          {{
                            percentageFormat(
                              controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Credit']]
                            )
                          }}
                        </p>
                        <input
                          v-if="isEdit['RateCredit'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="form-control text-center manual-input text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>

                    <td
                      :class="{
                        hoverColor: isActive['Elasticity'],
                        flagChangeAnimation: isChanged['Elasticity'],
                        flagChange: isChanged['Elasticity'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('Elasticity')"
                      @mouseleave="mouseLeaveController('Elasticity')"
                      @contextmenu.prevent="revertChange()"
                    >
                      <label class="font-bold">Volume Elasticity</label>
                      <div
                        class="cursor-pointer"
                        @dblclick="
                          isEdit['Elasticity'].isEditing = true;
                          tempInput =
                            controllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Elasticity']
                            ].toFixed(1);
                        "
                        @keyup.enter="keyupEnter"
                        @focusout="(e) => controllerUnitChange(e, 'Elasticity', 'Elasticity')"
                      >
                        <p v-if="!isEdit['Elasticity'].isEditing">
                          {{
                            controllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Elasticity']
                            ].toFixed(1)
                          }}
                        </p>
                        <input
                          v-if="isEdit['Elasticity'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="form-control text-center manual-input text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['Index'],
                        flagChangeAnimation: isChanged['Index'],
                        flagChange: isChanged['Index'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('Index')"
                      @mouseleave="mouseLeaveController('Index')"
                      @contextmenu.prevent="revertChange()"
                    >
                      <label class="font-bold">Indexation</label>
                      <div
                        class="cursor-pointer"
                        @dblclick="
                          isEdit['Index'].isEditing = true;
                          tempInput = (
                            controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Indexation']] *
                            100
                          ).toFixed(1);
                        "
                        @keyup.enter="keyupEnter"
                        @focusout="(e) => controllerUnitChange(e, 'Index', 'Indexation')"
                      >
                        <p v-if="!isEdit['Index'].isEditing">
                          {{
                            percentageFormat(
                              controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Indexation']]
                            )
                          }}
                        </p>
                        <input
                          v-if="isEdit['Index'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="form-control text-center manual-input text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>

                    <td
                      :class="{
                        hoverColor: isActive['CP'],
                        flagChangeAnimation: isChanged['CP'],
                        flagChange: isChanged['CP'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('CP')"
                      @mouseleave="mouseLeaveController('CP')"
                      @contextmenu.prevent="revertChange()"
                    >
                      <label class="font-bold">Manual Perturbation</label>
                      <div
                        class="cursor-pointer"
                        @dblclick="
                          isEdit['CP'].isEditing = true;
                          tempInput = (
                            controllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Manual Perturbation']
                            ] * 100
                          ).toFixed(1);
                        "
                        @keyup.enter="keyupEnter"
                        @focusout="(e) => controllerUnitChange(e, 'CP', 'Manual Perturbation')"
                      >
                        <p v-if="!isEdit['CP'].isEditing">
                          {{
                            percentageFormat(
                              controllerData['value_array'][CURRENT][
                                underwritingStore.valueArrayColumns['Manual Perturbation']
                              ]
                            )
                          }}
                        </p>
                        <input
                          v-if="isEdit['CP'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="form-control text-center manual-input text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>

                    <td
                      :class="{
                        hoverColor: isActive['PP'],
                        flagChangeAnimation: isChanged['PP'],
                        flagChange: isChanged['PP'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('PP')"
                      @mouseleave="mouseLeaveController('PP')"
                      @contextmenu.prevent="revertChange()"
                    >
                      <label class="font-bold">Alg. Perturbation</label>
                      <div class="cursor-pointer" @keyup.enter="keyupEnter">
                        <p v-if="!isEdit['PP'].isEditing">
                          {{
                            percentageFormat(
                              controllerData['value_array'][CURRENT][
                                underwritingStore.valueArrayColumns['Algorithmic Perturbation']
                              ]
                            )
                          }}
                        </p>
                        <input
                          v-if="isEdit['PP'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="form-control text-center manual-input text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3" class="bg-sybil-teal bg-opacity-20 pl-2">
                      <i>Results, double click to change <b>Commission</b> or <b>Loads</b></i>
                    </td>
                  </tr>
                  <tr class="border-b">
                    <td
                      :class="{
                        hoverColor: isActive['GWP'],
                        flagChangeAnimation:
                          (isChanged['RateChange'] || isChanged['Elasticity'] || isChanged['Index']) && isProjCurr,
                        flagChange:
                          (isChanged['RateChange'] || isChanged['Elasticity'] || isChanged['Index']) && isProjCurr,
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('GWP')"
                      @mouseleave="mouseLeaveController('GWP')"
                    >
                      <label class="font-bold">{{ isProjCurr ? 'Sys. Proj.' : 'Actual' }} GWP {{ amountType }}</label>
                      <p
                        v-if="!isEdit['GWP'].isEditing"
                        :class="{
                          edited: false,
                        }"
                      >
                        {{
                          gwpFormat(controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['GWP']])
                        }}
                      </p>
                    </td>

                    <td
                      :class="{
                        hoverColor: isActive['Att'],
                        flagChangeAnimation: isChanged['Att'],
                        flagChange: isChanged['Att'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('Att')"
                      @mouseleave="mouseLeaveController('Att')"
                    >
                      <label class="font-bold">Attritional A-priori</label>
                      <div
                        class="cursor-pointer"
                        @dblclick="
                          isEdit['Att'].isEditing = true;
                          tempInput = (
                            controllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] * 100
                          ).toFixed(1);
                        "
                        @keyup.enter="keyupEnter"
                        @focusout="(e) => controllerUnitChange(e, 'Att', 'Attritional A-priori')"
                      >
                        <p v-if="!isEdit['Att'].isEditing">
                          {{
                            percentageFormat(
                              controllerData['value_array'][CURRENT][
                                underwritingStore.valueArrayColumns['Attritional A-priori']
                              ]
                            )
                          }}
                        </p>
                        <input
                          v-if="isEdit['Att'].isEditing"
                          type="text"
                          :value="tempInput"
                          style="width: 100%; padding: 0; height: 80%; line-height: 100%; margin: auto"
                          class="text-center text-sm"
                          @focus="onFocusController"
                          @keyup="keyUpInput"
                        />
                      </div>
                    </td>

                    <td
                      :class="{
                        hoverColor: isActive['Commission'],
                        flagChangeAnimation: isChanged['Commission'],
                        flagChange: isChanged['Commission'],
                      }"
                      class="text-center"
                      @click="onDynamicModal('Commission')"
                      @mouseover="mouseOverCurrMonth('Commission')"
                      @mouseleave="mouseLeaveController('Commission')"
                    >
                      <label class="font-bold"> {{ isProjCurr ? 'Sys. Proj.' : 'Actual' }} Commission</label>
                      <div class="cursor-pointer">
                        {{
                          percentageFormat(
                            controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr class="border-b">
                    <td
                      :class="{
                        hoverColor: isActive['Loads'],
                        flagChangeAnimation: isChanged['Loads'],
                        flagChange: isChanged['Loads'],
                      }"
                      class="text-center border-r"
                      @click="onDynamicModal('Loads')"
                      @mouseover="mouseOverCurrMonth('Loads')"
                      @mouseleave="mouseLeaveController('Loads')"
                    >
                      <label class="font-bold">Loads</label>
                      <div class="cursor-pointer">
                        {{
                          percentageFormat(
                            portfolioStore.parameters['claims_nature']
                              .slice(1)
                              .map(
                                (x) =>
                                  controllerData['value_array'][CURRENT][
                                    underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                  ]
                              )
                              .reduce((s, a) => s + a, 0)
                          )
                        }}
                      </div>
                    </td>
                    <td
                      :class="{
                        hoverColor: isActive['NLR'],
                        flagChangeAnimation: isChanged['NLR'],
                        flagChange: isChanged['NLR'],
                      }"
                      class="text-center border-r"
                      @mouseover="mouseOverCurrMonth('NLR')"
                      @mouseleave="mouseLeaveController('NLR')"
                    >
                      <label class="font-bold">NLR A-priori</label>
                      <div class="">
                        {{
                          percentageFormat(
                            (controllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] +
                              portfolioStore.parameters['claims_nature']
                                .slice(1)
                                .map(
                                  (x) =>
                                    controllerData['value_array'][CURRENT][
                                      underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                    ]
                                )
                                .reduce((s, a) => s + a, 0)) /
                              (1 -
                                controllerData['value_array'][CURRENT][
                                  underwritingStore.valueArrayColumns['Commission']
                                ])
                          )
                        }}
                      </div>
                    </td>
                    <td
                      :class="{
                        hoverColor: isActive['CCR'],
                        flagChangeAnimation: isChanged['CCR'],
                        flagChange: isChanged['CCR'],
                      }"
                      class="text-center"
                      @mouseover="mouseOverCurrMonth('CCR')"
                      @mouseleave="mouseLeaveController('CCR')"
                    >
                      <label class="font-bold">CCR A-priori</label>
                      <div id="ccr" type="" class="modal-value-x ccr">
                        {{
                          percentageFormat(
                            controllerData['value_array'][CURRENT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] +
                              portfolioStore.parameters['claims_nature']
                                .slice(1)
                                .map(
                                  (x) =>
                                    controllerData['value_array'][CURRENT][
                                      underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                    ]
                                )
                                .reduce((s, a) => s + a, 0) +
                              controllerData['value_array'][CURRENT][underwritingStore.valueArrayColumns['Commission']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3" class="p-2 bg-sybil-teal bg-opacity-20">
                      <div><i>(i) Double click on numbers to change</i></div>
                      <div><i>(ii) Right click on numbers to discard change</i></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td v-if="controllerData['UNDERWRITING_MONTH'][NEXT]" style="width: 200px">
              <table
                class="shadow-lg scale-75 hover:scale-100 transition ease-in-out duration-500"
                style="background-color: #fff; width: 150px; margin: 0 auto; border: 1px solid #eee"
              >
                <tbody>
                  <tr>
                    <th class="bg-sybil-teal text-white cursor-pointer py-1">
                      {{ controllerData['UNDERWRITING_MONTH'][NEXT] }}
                    </th>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['GWP'],
                        flagChangeAnimation: isChanged['GWP'],
                        flagChange: isChanged['GWP'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('GWP')"
                      @mouseleave="mouseLeaveController('GWP')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        {{ isProjNext ? 'Sys. Proj.' : 'Actual' }}
                        GWP
                        {{ amountType }}
                        {{ gwpFormat(controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['GWP']]) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['Att'],
                        flagChangeAnimation: isChanged['Att'],
                        flagChange: isChanged['Att'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('Att')"
                      @mouseleave="mouseLeaveController('Att')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Att. A-priori:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['Commission'],
                        flagChangeAnimation: isChanged['Commission'],
                        flagChange: isChanged['Commission'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('Commission')"
                      @mouseleave="mouseLeaveController('Commission')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        {{ isProjNext ? 'Sys. Proj.' : 'Actual' }} Commission:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Commission']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['RateChange'] }"
                      class="border-b"
                      @mouseover="isActive['RateChange'] = true"
                      @mouseleave="isActive['RateChange'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Rate change:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Rate Change']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['RateCredit'] }"
                      class="border-b"
                      @mouseover="isActive['RateCredit'] = true"
                      @mouseleave="isActive['RateCredit'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Rate credit:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Credit']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['Elasticity'],
                        flagChangeAnimation: isChanged['Elasticity'],
                        flagChange: isChanged['Elasticity'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('Elasticity')"
                      @mouseleave="mouseLeaveController('Elasticity')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Elasticity:
                        {{
                          controllerData['value_array'][NEXT][
                            underwritingStore.valueArrayColumns['Elasticity']
                          ].toFixed(1)
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['Index'] }"
                      class="border-b"
                      @mouseover="isActive['Index'] = true"
                      @mouseleave="isActive['Index'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Index.:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Indexation']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['CP'] }"
                      class="border-b"
                      @mouseover="isActive['CP'] = true"
                      @mouseleave="isActive['CP'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        M. Perturbation:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][
                              underwritingStore.valueArrayColumns['Manual Perturbation']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{ hoverColor: isActive['PP'] }"
                      class="border-b"
                      @mouseover="isActive['PP'] = true"
                      @mouseleave="isActive['PP'] = false"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Alg. Perturbation:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][
                              underwritingStore.valueArrayColumns['Algorithmic Perturbation']
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['LineSize'],
                        flagChangeAnimation: isChanged['LineSize'],
                        flagChange: isChanged['LineSize'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('LineSize')"
                      @mouseleave="mouseLeaveController('LineSize')"
                    >
                      <div ref="LineSize+1" class="break-normal my-1 text-center cursor-default">
                        Line Size:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][
                              underwritingStore.valueArrayColumns[portfolioStore.selectedLineSize]
                            ]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['Loads'],
                        flagChangeAnimation: isChanged['Loads'],
                        flagChange: isChanged['Loads'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('Loads')"
                      @mouseleave="mouseLeaveController('Loads')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        Loads:
                        {{
                          percentageFormat(
                            portfolioStore.parameters['claims_nature']
                              .slice(1)
                              .map(
                                (x) =>
                                  controllerData['value_array'][NEXT][
                                    underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                  ]
                              )
                              .reduce((s, a) => s + a, 0)
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['NLR'],
                        flagChangeAnimation: isChanged['NLR'],
                        flagChange: isChanged['NLR'],
                      }"
                      class="border-b"
                      @mouseover="mouseOverCurrMonth('NLR')"
                      @mouseleave="mouseLeaveController('NLR')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        NLR A-priori:
                        {{
                          percentageFormat(
                            (controllerData['value_array'][NEXT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] +
                              portfolioStore.parameters['claims_nature']
                                .slice(1)
                                .map(
                                  (x) =>
                                    controllerData['value_array'][NEXT][
                                      underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                    ]
                                )
                                .reduce((s, a) => s + a, 0)) /
                              (1 -
                                controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Commission']])
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      :class="{
                        hoverColor: isActive['CCR'],
                        flagChangeAnimation: isChanged['CCR'],
                        flagChange: isChanged['CCR'],
                      }"
                      @mouseover="mouseOverCurrMonth('CCR')"
                      @mouseleave="mouseLeaveController('CCR')"
                    >
                      <div class="break-normal my-1 text-center cursor-default">
                        CCR A-priori:
                        {{
                          percentageFormat(
                            controllerData['value_array'][NEXT][
                              underwritingStore.valueArrayColumns['Attritional A-priori']
                            ] +
                              portfolioStore.parameters['claims_nature']
                                .slice(1)
                                .map(
                                  (x) =>
                                    controllerData['value_array'][NEXT][
                                      underwritingStore.valueArrayColumns[toTitleCase(x) + ' Load']
                                    ]
                                )
                                .reduce((s, a) => s + a, 0) +
                              controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Commission']]
                          )
                        }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <template #footer>
      <JetButton
        v-if="controllerData['UNDERWRITING_MONTH'] && controllerData['UNDERWRITING_MONTH'][NEXT]"
        style="background-color: rgb(245, 108, 108); margin-right: 15px"
        @click="ceaseUncease()"
        >{{
          controllerData['value_array'][NEXT][underwritingStore.valueArrayColumns['Exits']]
            ? 'Cease underwriting'
            : 'Uncease underwriting'
        }}</JetButton
      >
      <JetButton
        style="
          margin-right: 15px;
          background-color: #eeeeff;
          color: black;
          border: 1px solid #ccc;
          transition: background-color 0.3s ease;
          cursor: pointer;
        "
        @click="underwritingStore.updateIsUnderwritingControllerModal(false)"
        >Cancel</JetButton
      >
      <JetButton @click="submitUnderwritingController()">Confirm</JetButton>
    </template>
  </DialogComponent>
</template>
<style scoped>
.edited {
  color: red;
}

.labeltd {
  width: 33%;
  background-color: rgb(255, 255, 255);
}

.modalContentSmall {
  width: 33%;
  text-align: center;
  background-color: rgb(255, 255, 255);
}

.hoverColor {
  color: #ff5555;
}

.modal-bu:hover {
  color: #109cff;
}

.png-teal:hover {
  filter: invert(63%) sepia(42%) saturate(423%) hue-rotate(105deg) brightness(92%) contrast(93%);
}

.png-gray {
  filter: invert(69%) sepia(0%) saturate(0%) hue-rotate(220deg) brightness(89%) contrast(89%);
}

.projection {
  background-color: black;
}

.p-datatable-thead {
  color: rgb(85, 182, 145);
}

.textdarea:focus {
  background-color: lightblue;
}

@keyframes change-color {
  0% {
    background-color: #5555ff;
  }

  100% {
    background-color: #eeeeff;
  }
}

.flagChangeAnimation {
  animation-name: change-color;
  animation-duration: 1s;
  animation-iteration-count: 1;
}

.flagChange {
  background-color: #eeeeff;
}

.bghover::hover {
  background-color: #eef8f4;
  width: fit-content;
}

.p-autocomplete {
  width: 100%;
}

.p-inputgroup-addon:first-child,
.p-inputgroup button:first-child,
.p-inputgroup input:first-child,
.p-inputgroup > .p-inputwrapper:first-child,
.p-inputgroup > .p-inputwrapper:first-child > .p-inputtext {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.p-inputtext,
.p-component,
.p-inputnumber-input {
  border-top-left-radius: 0rem;
  border-bottom-left-radius: 0rem;
}

.p-inputgroup-addon:last-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
</style>
